import { ActivityFolder } from './../../activity-save-folder/interfaces/activity-folder';
import { Injectable } from '@angular/core';
import { ActivityStateFolderService } from '../../activity-save-folder';
import {
  CompleteActivityState as OldCompleteActivityState,
  CompleteActivityStateService,
  IncompleteActivityStateService,
  CompletedActivity,
} from '../..';
import { ActivityState as NewCompleteActivityState } from '../../activity-save-folder';

import { IncompleteActivityState as OldIncompleteActivityState } from '../..';
import { IncompleteActivityState as NewIncompleteActivityState } from '../../activity-save-folder/interfaces/incomplete-activity-state';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import {
  ProgressNodeFocusEnum,
  ProgressNodeStatusEnum,
  ProgressNodeTypeEnum,
} from 'src/app/modules/progress-bar/enums';
import { AnswerStateEnum } from 'src/app/shared/enums';
import { Activity } from 'src/app/shared/interfaces';
import { IProgressNodeData } from 'src/app/modules/progress-bar/interfaces/progress-node-data.interface';
import { ActivityManifestObject } from 'src/app/shared/interfaces/activity-manifest-object';
import { ActivityObjectTypes } from 'src/app/shared/enums/activity-object-types';

@Injectable({
  providedIn: 'root',
})
export class ActivityStateManagerService {
  private saveDataFolder = false;
  private activityCreationIndex = 0;
  private activityCreationQuestionIndex = 0;

  constructor(
    private incompleteActivityStateService: IncompleteActivityStateService,
    private completeActivityStateService: CompleteActivityStateService,
    private activityStateFolderService: ActivityStateFolderService,
    private featureFlagService: FeatureFlagService
  ) {
    this.saveDataFolder = this.featureFlagService.isFlagEnabled(
      FeatureFlags.SAVE_DATA_FOLDER
    );
    this.featureFlagService.flagsChanged.subscribe(() => {
      this.saveDataFolder = this.featureFlagService.isFlagEnabled(
        FeatureFlags.SAVE_DATA_FOLDER
      );
    });
  }

  //#region Incomplete State
  setStoragePrefix(): void {
    this.incompleteActivityStateService.setStoragePrefix();
  }

  async saveIncompleteActivity(
    activityState: OldIncompleteActivityState
  ): Promise<void> {
    if (!this.saveDataFolder) {
      return this.incompleteActivityStateService.saveActivity(activityState);
    } else {
      return this.activityStateFolderService.addIncompleteActivity(
        activityState.id,
        this.createNewIncompleteFromOld(activityState)
      );
    }
  }

  async removeIncompleteActivity(activityId: string): Promise<void> {
    if (!this.saveDataFolder) {
      return this.incompleteActivityStateService.removeActivity(activityId);
    } else {
      return this.activityStateFolderService.removeIncompleteActivity(
        activityId
      );
    }
  }

  /**
   * Export the current activity's state
   * @returns IncompleteActivityState representing current activity state
   */
  /**
   * Get string array of activity id's we have activity states saved for.
   * @returns String array representing all id's we have activity states saved for
   */
  async getSavedActivityIds(): Promise<string[]> {
    if (!this.saveDataFolder) {
      return this.incompleteActivityStateService.getSavedActivityIds();
    } else {
      return (
        await this.activityStateFolderService.getIncompleteActivities()
      ).map(activity => activity.activityId);
    }
  }

  /**
   * Gets the saved state data from the passed in activity Id if it exists
   * @param activityId The id of the activity we want to get the saved state for
   * @returns IncompleteActivityState if it exists, undefined if it doesn't
   */
  async getIncompleteActivityState(
    activityId: string
  ): Promise<OldIncompleteActivityState> {
    if (!this.saveDataFolder) {
      return this.incompleteActivityStateService.getSavedActivityState(
        activityId
      );
    } else {
      return this.activityStateFolderService
        .getIncompleteActivity(activityId)
        .then(activityState => {
          if (activityState) {
            return this.createOldIncompleteFromNew(activityState);
          } else {
            return Promise.reject('No activity state found');
          }
        });
    }
  }

  /**
   * Gets an array of all saved activity states
   * @returns Array of IncompleteActivityState objects for all saved states
   */
  async getSavedActivityStates(): Promise<OldIncompleteActivityState[]> {
    if (!this.saveDataFolder) {
      return this.incompleteActivityStateService.getSavedActivityStates();
    } else {
      const folderStates =
        await this.activityStateFolderService.getIncompleteActivities();
      return folderStates.map(activity =>
        this.createOldIncompleteFromNew(activity)
      );
    }
  }

  /**
   * Creates an activity state on the first step based on the activity provided
   * @param activity The activity to create the default activity state for
   * @returns An Activity state on the first step
   */
  createDefaultActivityState(activity: Activity): OldIncompleteActivityState {
    return {
      id: activity.id,
      currentStep: -1,
      numPerfect: 0,
      numCorrect: 0,
      numIncorrect: 0,
      questionNumber: 0,
      answerStates: [],
      populationResponses: [],
      progressNodes: this.createProgressNodesFromManifest(
        activity.activityManifest
      ),
      elapsedTime: 0,
      isAssignment: false,
      currentActivityOid: -1,
      startTime: new Date().toISOString(),
      clickstreamInteractions: [],
    };
  }

  private createProgressNodesFromManifest(
    manifest: ActivityManifestObject[]
  ): IProgressNodeData[] {
    const nodes: IProgressNodeData[] = [];
    this.activityCreationIndex = 0;
    this.activityCreationQuestionIndex = 0;
    for (let i = 0; i < manifest.length; i++) {
      const node = this.getNodeFromManifestObject(
        manifest[i],
        i === 0 ? ProgressNodeFocusEnum.active : ProgressNodeFocusEnum.inactive,
        ProgressNodeStatusEnum.none
      );
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  private getNodeFromManifestObject(
    manifestObject: ActivityManifestObject,
    index: number,
    focus?: number,
    status?: number
  ): IProgressNodeData | undefined {
    switch (manifestObject.type) {
      case ActivityObjectTypes.VIDEO:
        return {
          index: this.activityCreationIndex++,
          type: ProgressNodeTypeEnum.videoNodeComp,
          data: {},
          focus: focus || ProgressNodeFocusEnum.inactive,
          status: 0,
        };
      case ActivityObjectTypes.QUESTION: {
        const currIndex = this.activityCreationQuestionIndex + 1;
        this.activityCreationQuestionIndex++;
        return {
          index: this.activityCreationIndex++,
          type: ProgressNodeTypeEnum.progressNodeComp,
          data: { text: currIndex.toString() },
          focus: focus || ProgressNodeFocusEnum.inactive,
          status: status || ProgressNodeStatusEnum.none,
        };
      }
      default:
        return undefined;
    }
  }

  private createNewIncompleteFromOld(
    oldIncomplete: OldIncompleteActivityState
  ): NewIncompleteActivityState {
    return {
      activityId: oldIncomplete.id,
      startTime: oldIncomplete.startTime,
      secondsToComplete: oldIncomplete.elapsedTime,
      lastOpened: oldIncomplete.lastOpened,
      isAssignment: oldIncomplete.isAssignment,
      progressNodes: oldIncomplete.progressNodes,
      currentStep: oldIncomplete.currentStep,
      populationResponses: oldIncomplete.populationResponses,
      filledAnswers: oldIncomplete.filledAnswers,
      selectedAnswers: oldIncomplete.selectedAnswers,
      disabledAnswers: oldIncomplete.disabledAnswers,
      curHintsRevealed: oldIncomplete.curHintsRevealed,
      activitySeed: oldIncomplete.activitySeed,
      currentActivityOid: oldIncomplete.currentActivityOid,
      clickstreamInteractions: oldIncomplete.clickstreamInteractions,
    } as NewIncompleteActivityState;
  }

  private createOldIncompleteFromNew(
    newIncomplete: NewIncompleteActivityState
  ): OldIncompleteActivityState {
    return {
      id: newIncomplete.activityId,
      currentStep: newIncomplete.currentStep,
      numPerfect: newIncomplete.progressNodes.filter(
        node =>
          node.type === ProgressNodeTypeEnum.progressNodeComp &&
          node.status === ProgressNodeStatusEnum.perfect
      ).length,
      numCorrect: newIncomplete.progressNodes.filter(
        node =>
          node.type === ProgressNodeTypeEnum.progressNodeComp &&
          node.status === ProgressNodeStatusEnum.correct
      ).length,
      numIncorrect: newIncomplete.progressNodes.filter(
        node =>
          node.type === ProgressNodeTypeEnum.progressNodeComp &&
          node.status === ProgressNodeStatusEnum.wrong
      ).length,
      questionNumber: newIncomplete.populationResponses.length,
      answerStates: newIncomplete.progressNodes
        .filter(node => node.type === ProgressNodeTypeEnum.progressNodeComp)
        .map(node => this.translateProgressStatusToAnswerState(node.status)),
      populationResponses: newIncomplete.populationResponses,
      progressNodes: newIncomplete.progressNodes,
      elapsedTime: newIncomplete.secondsToComplete,
      currTry:
        newIncomplete.populationResponses[
          newIncomplete.populationResponses.length - 1
        ].responses.length,
      filledAnswers: newIncomplete.filledAnswers,
      selectedAnswers: newIncomplete.selectedAnswers,
      disabledAnswers: newIncomplete.disabledAnswers,
      curHintsRevealed: newIncomplete.curHintsRevealed,
      isAssignment: newIncomplete.isAssignment,
      lastOpened: newIncomplete.lastOpened,
      activitySeed: newIncomplete.activitySeed,
      currentActivityOid: newIncomplete.currentActivityOid,
      startTime: newIncomplete.startTime,
      clickstreamInteractions: newIncomplete.clickstreamInteractions,
    };
  }

  private translateProgressStatusToAnswerState(
    progressNodeStatus: ProgressNodeStatusEnum
  ): AnswerStateEnum {
    switch (progressNodeStatus) {
      case ProgressNodeStatusEnum.none:
        return AnswerStateEnum.INCOMPLETE;
      case ProgressNodeStatusEnum.perfect:
        return AnswerStateEnum.PERFECT;
      case ProgressNodeStatusEnum.correct:
        return AnswerStateEnum.CORRECT;
      case ProgressNodeStatusEnum.wrong:
        return AnswerStateEnum.INCORRECT;
      case ProgressNodeStatusEnum.complete:
        return AnswerStateEnum.COMPLETE;
    }
  }
  //#endregion

  //#region Complete State
  async addCompleteActivityStateAndSync(
    activityState: OldCompleteActivityState,
    shouldSync = true
  ): Promise<void> {
    if (!this.saveDataFolder) {
      return this.completeActivityStateService.addActivityStateAndSync(
        activityState,
        shouldSync
      );
    } else {
      return this.activityStateFolderService.addCompleteActivity(
        this.createNewCompleteFromOld(activityState)
      );
    }
  }

  async getCompletedActivities(): Promise<CompletedActivity[] | undefined> {
    if (!this.saveDataFolder) {
      return this.completeActivityStateService.getCompletedActivities();
    } else {
      const folders =
        await this.activityStateFolderService.getActivityFolders();

      return folders.map(folder =>
        this.createCompletedActivityFromActivityFolder(folder)
      );
    }
  }

  async getActivityRuns(
    activityId: string
  ): Promise<OldCompleteActivityState[] | undefined> {
    if (!this.saveDataFolder) {
      return this.completeActivityStateService.getActivityRuns(activityId);
    } else {
      return (
        await this.activityStateFolderService.getCompleteActivities(activityId)
      ).map(activity => this.createOldCompleteFromNew(activity));
    }
  }

  async addActivityState(
    activityState: OldCompleteActivityState
  ): Promise<void> {
    if (!this.saveDataFolder) {
      return this.completeActivityStateService.addActivityState(activityState);
    } else {
      return this.activityStateFolderService.addCompleteActivity(
        this.createNewCompleteFromOld(activityState)
      );
    }
  }

  private createCompletedActivityFromActivityFolder(
    activityFolder: ActivityFolder
  ): CompletedActivity {
    return {
      activityId: activityFolder.activityId,
      timesCompleted:
        activityFolder.completions.length +
        activityFolder.assignmentCompletions.length,
      maxStars: activityFolder.maxStars,
      fastestTimeInSecs: activityFolder.fastestTimeInSecs,
      completions: [
        ...activityFolder.completions,
        ...activityFolder.assignmentCompletions,
      ].map(completion => this.createOldCompleteFromNew(completion)),
    };
  }

  private createOldCompleteFromNew(
    completeActivity: NewCompleteActivityState
  ): OldCompleteActivityState {
    return {
      activityId: completeActivity.activityId,
      dateCompleted: completeActivity.lastOpened,
      timeToCompleteSecs: completeActivity.secondsToComplete,
      starsEarned: completeActivity.starsEarned ?? 0,
      score: completeActivity.score ?? 0,
      answerStates: completeActivity.progressNodes.map(node =>
        this.translateProgressStatusToAnswerState(node.status)
      ),
    };
  }

  private createNewCompleteFromOld(
    completeActivity: OldCompleteActivityState
  ): NewCompleteActivityState {
    return {
      activityId: completeActivity.activityId,
      startTime: new Date().toISOString(),
      secondsToComplete: completeActivity.timeToCompleteSecs,
      lastOpened: completeActivity.dateCompleted,
      starsEarned: completeActivity.starsEarned,
      score: completeActivity.score,
      isAssignment: completeActivity.isAssignment ?? false,
      progressNodes: [],
    };
  }
  //#endregion
}
